import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import ActionButton from "../../formInputs/buttons/ActionButton";
import OeAutoSuggest from "../../formInputs/autoSuggest/OeAutoSuggest";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import States from "../../../const/StatesConst";
import T from "../../translations/Translations";
import Tooltip from "react-bootstrap/Tooltip";

import { GlobalContext } from "../../contextProvider/ContextProvider";

/**
 * A form that shows all student information. First, last, address, gender ect
 * @param {func} birthdateReset
 * @param {bool} birthdateFlag
 * @param {{}} formState
 * @param {func} formDispatch
 * @param {func} handleChange
 * @param {bool} hasArcAddress
 * @param {func} setHasArcAddress
 * @param {func} showDobResetButton
 * @param {{}} studentIcData
 * @returns {JSX.Element}
 */
const StudentForm = ({
    birthdateReset,
    birthdateFlag,
    formState,
    formDispatch,
    handleChange,
    hasArcAddress,
    setHasArcAddress, // this is (true/false) to disable fields in StudentForm.
    showDobResetButton,
    studentIcData
}) => {
    const { state } = useContext(GlobalContext);
    const { studentNumber } = state || {};

    const [day, setDay] = useState("");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");

    const oeFormDay = T({ key: "OE_Form_Day" });
    const oeFormMonth = T({ key: "OE_Form_Month" });
    const oeFormYear = T({ key: "OE_Form_Year" });
    const oeFormExample = T({ key: "OE_Form_Example" });
    const oeFormClearBirthdate = T({ key: "OE_Form_Clear_Birthdate" });
    const oeFormStudentAddress = T({ key: "OE_Form_Student_Address" });
    const oeFormStudentAddressDescription = T({ key: "OE_Form_Student_Address_Description" });
    const oeFormSearchAddress = T({ key: "OE_Form_Search_Address" });
    const oeFormSelect = T({ key: "OE_Form_Select" });

    const getIsDisabled = () => {
        const { birthDay, birthMonth } = formState;
        if (birthDay?.length === 0 || birthMonth?.length === 0) {
            return true;
        }

        return formState.birthdate?.length > 0 || birthdateFlag;
    };

    const getTooltip = () => {
        return <span>Please fill in the Month and Day fields first!</span>;
    };

    useEffect(() => {
        if (formState.birthdate && formState.birthdate?.length > 0) {
            const parsedBirthday = formState.birthdate.split("-");
            if (parsedBirthday?.length > 0) {
                setMonth(parsedBirthday[1]);
                setDay(parsedBirthday[2]);
                setYear(parsedBirthday[0]);
            }
        }
    }, [day, formState, month, year]);

    return (
        <div className="gutter-90">
            <div className="student-info-container-outside">
                <div className="student-info-container">
                    <div className="input-container">
                        <label id="label" htmlFor="firstName">
                            {T({ key: "OE_Form_FirstName" })}: *
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber}
                            id="firstName"
                            name="firstName"
                            onChange={handleChange}
                            type="text"
                            value={formState?.firstName || ""}
                        />
                        <div className="input-subtext">{T({ key: "OE_Form_FirstName_Description" })}</div>
                    </div>
                    <div className="input-container">
                        <label id="label" htmlFor="middleName">
                            {T({ key: "OE_Form_MiddleName" })}:
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber}
                            id="middleName"
                            name="middleName"
                            onChange={handleChange}
                            type="text"
                            value={formState?.middleName || ""}
                        />
                        <div className="input-subtext">{T({ key: "OE_Form_MiddleName_Description" })}</div>
                    </div>
                    <div className="input-container">
                        <label id="label" htmlFor="lastName">
                            {T({ key: "OE_Form_LastName" })}: *
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber}
                            id="lastName"
                            name="lastName"
                            onChange={handleChange}
                            type="text"
                            value={formState?.lastName || ""}
                        />
                        <div className="input-subtext">{T({ key: "OE_Form_LastName_Description" })}</div>
                    </div>
                    <div className="input-container">
                        <div id="label">{T({ key: "OE_Form_Gender" })}: *</div>
                        <div className="radio-values-container">
                            <input
                                checked={formState.gender === "M"}
                                disabled={studentNumber}
                                id="male"
                                name="gender"
                                onChange={handleChange}
                                type="radio"
                                value="M"
                            />
                            <label className="radio-label" htmlFor="male">
                                {T({ key: "OE_Form_Gender_Male" })}
                            </label>
                        </div>
                        <div className="radio-values-container">
                            <input
                                checked={formState.gender === "F"}
                                disabled={studentNumber}
                                id="female"
                                name="gender"
                                onChange={handleChange}
                                type="radio"
                                value="F"
                            />
                            <label className="radio-label" htmlFor="female">
                                {T({ key: "OE_Form_Gender_Female" })}
                            </label>
                        </div>
                        <div className="radio-values-container">
                            <input
                                checked={formState.gender === "N"}
                                disabled={studentNumber}
                                id="none"
                                name="gender"
                                onChange={handleChange}
                                type="radio"
                                value="N"
                            />
                            <label className="radio-label" htmlFor="none" value={formState.gender}>
                                {T({ key: "OE_Form_Gender_NonBinary" })}
                            </label>
                        </div>
                    </div>
                    <div className="top-label dob-label">{T({ key: "OE_Form_Birthdate" })}: *</div>
                    <div className="input-container">
                        {studentIcData?.birthdate && (
                            <div className="dob-container">
                                <div className="dob-group">
                                    <label htmlFor="birthMonth">{oeFormMonth} *</label>
                                    <input
                                        className={studentNumber ? "disabledColor" : ""}
                                        disabled={formState.birthdate?.length > 0 || birthdateFlag}
                                        id="birthMonth"
                                        maxLength={2}
                                        name="birthMonth"
                                        onChange={handleChange}
                                        type="text"
                                        value={month} // see state
                                    />
                                    <div className="input-subtext">{oeFormExample}: MM</div>
                                </div>
                                <div className="dob-group">
                                    <label htmlFor="birthDay">{oeFormDay} *</label>
                                    <input
                                        className={studentNumber ? "disabledColor" : ""}
                                        disabled={formState.birthdate?.length > 0 || birthdateFlag}
                                        id="birthDay"
                                        maxLength={2}
                                        name="birthDay"
                                        onChange={handleChange}
                                        type="text"
                                        value={day} // see state
                                    />
                                    <div className="input-subtext">{oeFormExample}: DD</div>
                                </div>
                                <div className="dob-group">
                                    <label htmlFor="birthYear">{oeFormYear} *</label>
                                    <input
                                        className={studentNumber ? "disabledColor" : ""}
                                        disabled={formState.birthdate?.length > 0 || birthdateFlag}
                                        type="text"
                                        id="birthYear"
                                        maxLength={4}
                                        name="birthYear"
                                        onChange={handleChange}
                                        value={year} // see state
                                    />
                                    <div className="input-subtext">{oeFormExample}: YYYY</div>
                                </div>
                            </div>
                        )}
                        {!studentIcData && (
                            <div className="dob-container">
                                <div className="dob-group">
                                    <label htmlFor="birthMonth">{oeFormMonth} *</label>
                                    <input
                                        disabled={formState.birthdate?.length > 0 || birthdateFlag}
                                        id="birthMonth"
                                        name="birthMonth"
                                        onChange={handleChange}
                                        type="text"
                                        value={formState.birthMonth}
                                    />
                                    <div className="input-subtext">{oeFormExample}: MM</div>
                                </div>
                                <div className="dob-group">
                                    <label htmlFor="birthDay">{oeFormDay} *</label>
                                    <input
                                        disabled={formState.birthdate?.length > 0 || birthdateFlag}
                                        id="birthDay"
                                        name="birthDay"
                                        onChange={handleChange}
                                        type="text"
                                        value={formState.birthDay}
                                    />
                                    <div className="input-subtext">{oeFormExample}: DD</div>
                                </div>
                                {formState?.birthDay?.length === 0 || formState?.birthMonth?.length === 0 ? (
                                    <OverlayTrigger
                                        delay={{ hide: 5, show: 300 }}
                                        overlay={(props) => (
                                            <Tooltip className="tooltip-school" {...props}>
                                                {getTooltip()}
                                            </Tooltip>
                                        )}
                                        placement="top"
                                    >
                                        <div className="dob-group">
                                            <label htmlFor="birthYear">{oeFormYear} *</label>
                                            <input
                                                disabled={getIsDisabled()}
                                                id="birthYear"
                                                maxLength={4}
                                                name="birthYear"
                                                onChange={handleChange}
                                                type="text"
                                                value={formState.birthYear}
                                            />
                                            <div className="input-subtext">{oeFormExample}: YYYY</div>
                                        </div>
                                    </OverlayTrigger>
                                ) : (
                                    <div className="dob-group">
                                        <label htmlFor="birthYear">{oeFormYear} *</label>
                                        <input
                                            disabled={getIsDisabled()}
                                            id="birthYear"
                                            maxLength={4}
                                            name="birthYear"
                                            onChange={handleChange}
                                            type="text"
                                            value={formState.birthYear}
                                        />
                                        <div className="input-subtext">{oeFormExample}: YYYY</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {showDobResetButton && (
                        <div className="input-container">
                            <ActionButton
                                aira-label="Clear Birthdate Fields"
                                className="action-button-birthdate-reset"
                                label={oeFormClearBirthdate}
                                onClick={() => {
                                    birthdateReset();
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="student-info-container">
                    {!studentNumber && (
                        <div className="student-legal-address-container">
                            <div className="top-label">{oeFormStudentAddress}</div>
                            <div className="student-address-text">{oeFormStudentAddressDescription}</div>
                            <div className="input-container ">
                                <OeAutoSuggest
                                    formDispatch={formDispatch}
                                    formState={formState}
                                    hasArcAddress={hasArcAddress}
                                    id="searchAddress"
                                    label={oeFormSearchAddress}
                                    name="searchAddress"
                                    setHasArcAddress={setHasArcAddress}
                                />
                            </div>
                        </div>
                    )}
                    <div className="input-container">
                        <label id="label" htmlFor="address1">
                            {T({ key: "OE_Form_Address1" })}: *
                        </label>
                        <div className="search-address-container">
                            <input
                                className={studentNumber ? "disabledColor" : ""}
                                disabled={studentNumber || hasArcAddress}
                                type="text"
                                id="address1"
                                name="addressLine1"
                                onChange={handleChange}
                                value={formState?.addressLine1 || ""}
                            />
                        </div>
                        <div className="input-subtext">{oeFormExample}: 1234 Pine Lake Ave</div>
                    </div>
                    <div className="input-container">
                        <label id="label" className="" htmlFor="address2">
                            {T({ key: "OE_Form_Address2" })}:
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber}
                            type="text"
                            id="address2"
                            name="addressLine2"
                            onChange={handleChange}
                            value={formState?.addressLine2 || ""}
                        />
                        <div className="input-subtext">
                            {oeFormExample}: {T({ key: "OE_Form_Apt_Or_Unit" })}
                        </div>
                    </div>
                    <div className="input-container">
                        <label id="label" htmlFor="city">
                            {T({ key: "OE_Form_City" })}: *
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber || hasArcAddress}
                            id="city"
                            type="text"
                            name="city"
                            onChange={handleChange}
                            value={formState?.city || ""}
                        />
                        <div className="input-subtext">{oeFormExample}: Castle Rock</div>
                    </div>
                    <div className="input-container">
                        <label id="label" htmlFor="state">
                            {T({ key: "OE_Form_State" })}: *
                        </label>
                        {studentNumber && (
                            <input
                                className="disabledColor"
                                disabled
                                id="state"
                                name="state"
                                type="text"
                                value={formState?.state || "CO"}
                            />
                        )}
                        {!studentNumber && (
                            <select
                                disabled={hasArcAddress}
                                id="state"
                                name="state"
                                onChange={handleChange}
                                required
                                value={formState?.state}
                            >
                                <option value="">{oeFormSelect}</option>
                                {States &&
                                    States.map((obj, index) => {
                                        const optionKey = `opt-${index}`;
                                        return (
                                            <option key={optionKey} value={obj.value}>
                                                {obj.display}
                                            </option>
                                        );
                                    })}
                            </select>
                        )}
                    </div>
                    <div className="input-container">
                        <label id="label" htmlFor="zip">
                            {T({ key: "OE_Form_Zip" })}: *
                        </label>
                        <input
                            className={studentNumber ? "disabledColor" : ""}
                            disabled={studentNumber || hasArcAddress}
                            id="zip"
                            maxLength={5}
                            name="zip"
                            onChange={handleChange}
                            type="text"
                            value={formState?.zip || ""}
                        />
                        <div className="input-subtext">{oeFormExample}: 80662</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

StudentForm.propTypes = {
    birthdateFlag: PropTypes.bool,
    birthdateReset: PropTypes.func,
    formDispatch: PropTypes.func.isRequired,
    formState: PropTypes.oneOfType([PropTypes.object]).isRequired,
    handleChange: PropTypes.func.isRequired,
    hasArcAddress: PropTypes.bool,
    setHasArcAddress: PropTypes.func,
    showDobResetButton: PropTypes.bool,
    studentIcData: PropTypes.oneOfType([PropTypes.object])
};

StudentForm.defaultProps = {
    birthdateFlag: false,
    birthdateReset: null,
    hasArcAddress: false,
    setHasArcAddress: null,
    showDobResetButton: false,
    studentIcData: null
};

export default StudentForm;
